import { useEffect, useMemo, useState } from "react";
import {
  publicApi,
  type StatusResponse,
  type IncidentsResponse,
  type UptimeDailyResponse,
  type UptimeDayPoint,
} from "../../lib/api";
import { OverallBanner } from "../../components/StatusPill";
import { ComponentGrid } from "../../components/ComponentGrid";
import { IncidentList } from "../../components/IncidentList";
import { UpcomingMaintenance } from "../../components/UpcomingMaintenance";
import { UptimeBars } from "../../components/UptimeBars";

export function PublicHome() {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [active, setActive] = useState<IncidentsResponse | null>(null);
  const [uptime, setUptime] = useState<UptimeDailyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [s, i] = await Promise.all([
          publicApi.status(),
          publicApi.activeIncidents(),
        ]);
        if (cancelled) return;
        setStatus(s);
        setActive(i);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "unknown_error");
      }
    }
    void load();
    const id = window.setInterval(load, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Daily uptime is pulled less often - the backing rollup table is
  // refreshed once a day by daily-uptime-rollup.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const u = await publicApi.uptimeDaily(90);
        if (!cancelled) setUptime(u);
      } catch {
        // Non-fatal: the component grid still renders without bars.
      }
    }
    void load();
    const id = window.setInterval(load, 10 * 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const daysByComponent = useMemo(() => {
    const out: Record<string, UptimeDayPoint[]> = {};
    if (!uptime) return out;
    for (const c of uptime.components) {
      out[c.component_id] = c.days;
    }
    return out;
  }, [uptime]);

  const groups = useMemo(() => {
    if (!status) return null;
    const integrations = status.components.core.filter(
      (c) => c.group === "integrations",
    );
    const core = status.components.core.filter(
      (c) => c.group !== "integrations",
    );
    const upstream = status.components.upstream;
    // Maintenance is planned and surfaced separately, so it is not
    // counted as a dependency being "affected".
    const upstreamAffected = upstream.filter(
      (c) =>
        c.status === "degraded" ||
        c.status === "partial_outage" ||
        c.status === "major_outage",
    ).length;
    return { core, integrations, upstream, upstreamAffected };
  }, [status]);

  if (error) {
    return (
      <section className="mx-auto max-w-5xl px-4 py-10">
        <OverallBanner status="degraded" />
        <p className="mt-4 text-sm text-slate-700">
          Live status is temporarily unavailable. Subscribe to the{" "}
          <a className="text-blue-600 underline" href="/v1/feed.xml">
            Atom feed
          </a>{" "}
          to be notified when incidents are posted.
        </p>
      </section>
    );
  }

  if (!status || !groups) {
    return (
      <section className="mx-auto max-w-5xl px-4 py-10" aria-busy>
        <p className="text-slate-600">Loading status&hellip;</p>
      </section>
    );
  }

  const barsFor = (component_id: string) => {
    const days = daysByComponent[component_id];
    if (!days) return null;
    return <UptimeBars days={days} expectedLength={90} />;
  };

  return (
    <section className="mx-auto max-w-5xl px-4 py-10">
      <OverallBanner
        status={status.overall_status}
        upstreamAffected={groups.upstreamAffected}
      />
      <UpcomingMaintenance />
      <IncidentList
        title="Active incidents"
        incidents={active?.incidents ?? []}
        emptyMessage={
          groups.upstreamAffected > 0
            ? "No active incidents. Some third-party dependencies are affected - see below."
            : "No active incidents."
        }
      />
      <ComponentGrid
        title="Core services"
        components={groups.core}
        renderExtras={(c) => barsFor(c.component_id)}
      />
      {groups.integrations.length > 0 && (
        <ComponentGrid
          title="Integrations"
          components={groups.integrations}
          renderExtras={(c) => barsFor(c.component_id)}
        />
      )}
      <ComponentGrid
        title="Upstream dependencies"
        components={groups.upstream}
        renderExtras={(c) => barsFor(c.component_id)}
      />
      <p className="mt-8 text-xs text-slate-500">
        See the{" "}
        <a className="text-blue-600 underline" href="/archive">
          archive
        </a>{" "}
        for past incidents.
      </p>
    </section>
  );
}
